import type { MantineColorsTuple, MantineThemeOverride } from '@mantine/core'
import { translate } from 'i18n/i18n'
import { colors } from './colors'
import { HUB_CONNECTOR_ENVIRONMENT_IS_SWAGWORLD } from './hubConnectorEnvironment'

// Mantine expects 10 shades and builds its filled, light and outline variants from shade 6, so pad with
// the last defined shade
const shades = (...values: string[]): MantineColorsTuple => {
  const lastValue = values[values.length - 1]

  return [...values, ...Array(10 - values.length).fill(lastValue)] as unknown as MantineColorsTuple
}

// Mantine 8 takes the clear X out of the tab order and hides it from screen readers, so keyboard users couldn't clear a
// select. A function so the label follows the current language
const clearButtonProps = () => ({
  'tabIndex': 0,
  'aria-hidden': undefined,
  'aria-label': translate.phrases.banyanApp('Clear'),
})

export const customTheme: MantineThemeOverride = {
  // Agworld's root font size is 8px, so Swagworld doubles Mantine's rem sizes back to their 16px-root size
  scale: HUB_CONNECTOR_ENVIRONMENT_IS_SWAGWORLD ? 2 : 1,
  respectReducedMotion: true,
  // Mantine's default antialiasing renders text thinner on macOS than the browser default
  fontSmoothing: false,
  fontFamily: 'Roboto, Helvetica Neue, Helvetica, Arial, sans-serif',
  black: colors.midnight,
  colors: {
    primary: shades(colors.primary, colors.primaryLight),
    secondary: shades(colors.secondary100, colors.secondary500),
    midnight: shades(colors.midnight),
    grey: shades(colors.grey50, colors.grey200, colors.grey500, colors.grey800),
    red: shades(colors.red),
    yellow: shades(colors.yellow),
    green: shades(colors.green),
    white: shades(colors.white),
    sidekick: shades(colors.almaDark, colors.almaBackground),
  },
  defaultRadius: 'sm',
  radius: {
    xs: '0.187rem',
    sm: '0.187rem',
  },
  breakpoints: {
    xs: '28em',
    sm: '44em',
    md: '56em',
    lg: '64em',
    xl: '75em',
  },
  headings: {
    sizes: {
      h1: {
        fontWeight: '700',
        lineHeight: '2rem',
      },
      h2: {
        fontWeight: '700',
        lineHeight: '2rem',
      },
      h3: {
        fontWeight: '700',
        lineHeight: '2rem',
      },
      h4: {
        fontWeight: '700',
        lineHeight: '2rem',
      },
      h5: {
        fontWeight: '700',
        lineHeight: '2rem',
      },
      h6: {
        fontWeight: '700',
        lineHeight: '2rem',
      },
    },
  },
  components: {
    ActionIcon: {
      defaultProps: {
        variant: 'subtle',
        color: 'gray',
      },
    },
    Badge: {
      defaultProps: {
        variant: 'light',
      },
    },
    Avatar: {
      styles: {
        // the translucent default vanishes on dark backgrounds, such as the layers count badge
        placeholder: {
          backgroundColor: 'var(--mantine-color-gray-1)',
          color: 'var(--mantine-color-gray-7)',
        },
      },
    },
    Popover: {
      defaultProps: {
        withinPortal: false, // keeps dropdowns inside the drawer or modal that opens them
        // Mantine 8 hides a dropdown whose field scrolls off screen, so a tall calendar vanished mid-scroll
        hideDetached: false,
      },
    },
    Menu: {
      defaultProps: {
        returnFocus: false, // returning focus to the trigger pulled it back behind modals that menu items open
      },
    },
    Tooltip: {
      defaultProps: {
        withinPortal: false,
      },
    },
    MultiSelect: {
      defaultProps: () => ({
        comboboxProps: { withinPortal: false },
        hidePickedOptions: true,
        clearButtonProps: clearButtonProps(),
      }),
    },
    InputWrapper: {
      defaultProps: {
        inputWrapperOrder: ['label', 'input', 'description', 'error'],
      },
      styles: {
        label: {
          color: colors.midnight,
          fontSize: '16px',
          fontWeight: 500,
          fontStyle: 'normal',
          lineHeight: '19px',
        },
        error: {
          color: colors.red,
          fontStyle: 'italic',
          fontSize: '14px',
          lineHeight: '16px',
          margin: '10px 0',
        },
      },
    },
    // Input borders and states live in mantine-overrides.css: a styles prop here renders inline and
    // would beat its :focus rule and callers' error classes
    PasswordInput: {
      defaultProps: {
        size: 'sm',
      },
    },
    Select: {
      defaultProps: () => ({
        comboboxProps: { withinPortal: false },
        withCheckIcon: false, // mantine-overrides.css fills the selected row instead
        allowDeselect: false, // otherwise re-clicking the selected option clears it
        clearButtonProps: clearButtonProps(),
      }),
    },
    Slider: { styles: { bar: { backgroundColor: colors.primary } } },
  },
}
